import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TopBar from '../TopBar';
import LoadingScreen from '../ui/LoadingScreen';
import AccountDeletionModal from '../AccountDeletionModal';
import EditProfileModal from '../EditProfileModal';
import ChangePasswordModal from '../ChangePasswordModal';
import ForgotPasswordModal from '../ForgotPasswordModal';

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(false);

  useEffect(() => {
    // Check if this is the user's own profile
    if (user && userId === user.id) {
      setIsOwnProfile(true);
    } else if (user && userId !== user.id) {
      setIsOwnProfile(false);
      // For now, redirect to own profile if trying to view someone else's
      // In the future, you could implement public profile viewing
      navigate(`/user/${user.id}`);
    }
  }, [user, userId, navigate]);

  // Show loading screen while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex flex-col">
        <TopBar />
        <div className="flex-1 flex justify-center items-center">
          <LoadingScreen
            title="Checking Authentication"
            message="Verifying your access..."
          />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-dark-900 flex flex-col">
        <TopBar />
        <div className="flex-1 flex justify-center items-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Please Log In</h1>
            <p className="text-dark-300">You need to be logged in to view profile pages.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <TopBar />

      <div className="flex-1 pt-16 lg:pt-16 p-4 pt-20 lg:pt-20">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-dark-300 hover:text-white hover:bg-dark-700 rounded-lg transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  {isOwnProfile ? 'Your Profile' : `${user.full_name || 'User'}'s Profile`}
                </h1>
                <p className="text-dark-300">
                  {isOwnProfile ? 'Manage your account settings and information' : 'User profile information'}
                </p>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="glass-effect rounded-2xl border border-dark-600 p-8 relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-accent-pink/20 to-transparent rounded-full blur-2xl"></div>
            <div className="relative">
              {/* Profile Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-4 mb-6">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-r from-accent-purple to-accent-pink flex items-center justify-center shadow-2xl">
                    <span className="text-white font-bold text-3xl">
                      {user.full_name ? user.full_name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="text-left">
                    <h2 className="text-2xl font-bold text-white mb-1">
                      {user.full_name || 'User'}
                    </h2>
                    <p className="text-dark-300 text-sm">
                      User ID: {user.id}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-green-400 text-sm">Active</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Personal Information */}
                <div className="glass-effect border border-dark-600 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">👤</span>
                    <h3 className="text-lg font-semibold text-white">Personal Information</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-dark-300 mb-2">
                        Full Name
                      </label>
                      <div className="px-4 py-3 bg-dark-800/50 border border-dark-600 rounded-xl text-white flex items-center justify-between">
                        <span>{user.full_name || 'Not provided'}</span>
                        {isOwnProfile && (
                          <button
                            onClick={() => setShowEditModal(true)}
                            className="text-accent-purple hover:text-accent-pink transition-colors text-sm"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark-300 mb-2">
                        Email Address
                      </label>
                      <div className="px-4 py-3 bg-dark-800/50 border border-dark-600 rounded-xl text-white flex items-center justify-between">
                        <span>{user.email}</span>
                        {isOwnProfile && (
                          <button
                            onClick={() => setShowEditModal(true)}
                            className="text-accent-purple hover:text-accent-pink transition-colors text-sm"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark-300 mb-2">
                        Email Status
                      </label>
                      <div className={`px-4 py-3 border rounded-xl flex items-center gap-2 ${user.email_confirmed
                          ? 'bg-green-500/10 border-green-500/20 text-green-400'
                          : 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'
                        }`}>
                        <span className={`w-2 h-2 rounded-full ${user.email_confirmed ? 'bg-green-400' : 'bg-yellow-400'
                          }`}></span>
                        {user.email_confirmed ? 'Verified' : 'Pending Verification'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Details */}
                <div className="glass-effect border border-dark-600 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">📅</span>
                    <h3 className="text-lg font-semibold text-white">Account Details</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-dark-300 mb-2">
                        Member Since
                      </label>
                      <div className="px-4 py-3 bg-dark-800/50 border border-dark-600 rounded-xl text-white">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : 'Unknown'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark-300 mb-2">
                        Account Status
                      </label>
                      <div className="px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                        Active
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark-300 mb-2">
                        Profile URL
                      </label>
                      <div className="px-4 py-3 bg-dark-800/50 border border-dark-600 rounded-xl text-white text-sm font-mono">
                        /user/{user.id}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Management - Only show for own profile */}
              {isOwnProfile && (
                <>
                  {/* Profile Management */}
                  <div className="glass-effect border border-dark-600 rounded-xl p-6 mb-6">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="text-2xl">✏️</span>
                      <h3 className="text-lg font-semibold text-white">Profile Management</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button
                        onClick={() => setShowEditModal(true)}
                        className="px-6 py-3 bg-accent-purple/10 border-2 border-accent-purple/20 text-accent-purple hover:bg-accent-purple/20 hover:border-accent-purple/40 transition-all duration-300 rounded-xl font-medium flex items-center gap-2 justify-center"
                      >
                        <span>✏️</span>
                        Edit Profile
                      </button>

                      <button
                        onClick={() => setShowPasswordModal(true)}
                        className="px-6 py-3 bg-blue-500/10 border-2 border-blue-500/20 text-blue-400 hover:bg-blue-500/20 hover:border-blue-500/40 transition-all duration-300 rounded-xl font-medium flex items-center gap-2 justify-center"
                      >
                        <span>🔒</span>
                        Change Password
                      </button>
                    </div>
                  </div>

                  {/* Account Actions */}
                  <div className="glass-effect border border-dark-600 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="text-2xl">⚙️</span>
                      <h3 className="text-lg font-semibold text-white">Account Actions</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <button
                        onClick={() => navigate('/')}
                        className="px-6 py-3 bg-green-500/10 border-2 border-green-500/20 text-green-400 hover:bg-green-500/20 hover:border-green-500/40 transition-all duration-300 rounded-xl font-medium flex items-center gap-2 justify-center"
                      >
                        <span>🏠</span>
                        Dashboard
                      </button>

                      <button
                        onClick={() => setShowForgotPasswordModal(true)}
                        className="px-6 py-3 bg-yellow-500/10 border-2 border-yellow-500/20 text-yellow-400 hover:bg-yellow-500/20 hover:border-yellow-500/40 transition-all duration-300 rounded-xl font-medium flex items-center gap-2 justify-center"
                      >
                        <span>📧</span>
                        Reset Password
                      </button>

                      <button
                        onClick={logout}
                        className="px-6 py-3 border-2 border-accent-purple text-accent-purple hover:bg-accent-purple hover:text-white transition-all duration-300 rounded-xl font-medium flex items-center gap-2 justify-center"
                      >
                        <span>🚪</span>
                        Logout
                      </button>

                      <button
                        onClick={() => setShowDeleteModal(true)}
                        className="px-6 py-3 bg-red-500/10 border-2 border-red-500/20 text-red-400 hover:bg-red-500/20 hover:border-red-500/40 transition-all duration-300 rounded-xl font-medium flex items-center gap-2 justify-center sm:col-span-2 lg:col-span-1"
                      >
                        <span>🗑️</span>
                        Delete Account
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Modals */}
              {showDeleteModal && (
                <AccountDeletionModal
                  isOpen={showDeleteModal}
                  onClose={() => setShowDeleteModal(false)}
                />
              )}

              {showEditModal && (
                <EditProfileModal
                  isOpen={showEditModal}
                  onClose={() => setShowEditModal(false)}
                />
              )}

              {showPasswordModal && (
                <ChangePasswordModal
                  isOpen={showPasswordModal}
                  onClose={() => setShowPasswordModal(false)}
                />
              )}

              {showForgotPasswordModal && (
                <ForgotPasswordModal
                  onClose={() => setShowForgotPasswordModal(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;